import React, { useState, useEffect, useRef, useCallback } from "react";
import { Search } from "lucide-react";
import { Link } from "react-router-dom";
import "./SearchForm.css";
import { useSearchProducts } from "../../hooks/useSearchProducts";

function SearchForm() {
  const [searchText, setSearchText] = useState("");
  const [filteredResults, setFilteredResults] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const searchRef = useRef(null);
  const debounceRef = useRef(null);

  const { data: products } = useSearchProducts();

  const filterProducts = useCallback(
    (text) => {
      if (!text?.trim() || !products) return []

      const searchLower = text.toLowerCase()

      return products.filter((product) => {
        const name = (product.name || "").toLowerCase()
        const author = (product.meta_data?.find((m) => m.key === "author")?.value || "").toLowerCase()
        const editor = (product.meta_data?.find((m) => m.key === "editor")?.value || "").toLowerCase()

        return name.includes(searchLower) || author.includes(searchLower) || editor.includes(searchLower)
      })
    },
    [products]
  )

  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    if (!searchText.trim()) {
      setFilteredResults([]);
      setShowDropdown(false);
      return;
    }

    debounceRef.current = setTimeout(() => {
      const results = filterProducts(searchText);
      setFilteredResults(results);
      setShowDropdown(true);
    }, 300);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [searchText, products, filterProducts]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleResultClick = () => {
    setSearchText("");
    setShowDropdown(false);
  };

  const handleInputFocus = () => {
    if (!searchText.trim()) return;
    const results = filterProducts(searchText);
    setFilteredResults(results);
    setShowDropdown(results.length > 0);
  };

  return (
    <div className="SearchForm" ref={searchRef}>
      <Search color="white" size={14} />
      <input
        placeholder="Search book name, author, edition..."
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
        onFocus={handleInputFocus}
        onClick={handleInputFocus}
      />
      {showDropdown && (
        <div className="search-dropdown">
          {filteredResults.length > 0 ? (
            filteredResults.map((product) => (
              <Link
                key={product.id}
                to={`/product/${product.slug.toLowerCase()}`}
                className="search-result-item"
                onClick={handleResultClick}
              >
                <img src={product.images?.[0]?.src} alt={product.name} className="search-result-image" />
                <div className="search-result-info">
                  <p className="search-result-name">{product.name}</p>
                  <p className="search-result-author">
                    {product.meta_data?.find((meta) => meta.key === "author")?.value || "Unknown Author"}
                  </p>
                </div>
              </Link>
            ))
          ) : (
            <div className="search-result-item no-results">No products found.</div>
          )}
        </div>
      )}
    </div>
  )
}

export default SearchForm;
